import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { CategoryItem } from 'interfaces/widget-types'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import CarouselItem from '../HomePageBanner/CarouselItem'
import { ToggleField } from 'components/styledFormElements/ToggleField'
import { SingleSelect } from 'components/styledFormElements/SingleSelect'
import { useGetCategoryData } from 'hooks/category-manage'
import { useNavigate } from 'react-router-dom'
import { useUploadFiles } from 'hooks/upload'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { CATEGORY_GRID_DEFAULTS, CTA_ACTION_OPTIONS, SORTING_OPTIONS, SOURCE_TYPE_OPTIONS } from 'constants/widget-constant'
import { categoryGridFormSchema } from 'validations/widgetFields.validation'
import { useNotificationBar } from 'context/notificationContext';
import { getImageSrc, getRelativeIconPath } from 'services/util'
import { Upload } from 'lucide-react';
import { X } from 'lucide-react'
import IconDropdown from 'components/styledFormElements/IconDropdown'
import { useGetIcons } from 'hooks/useIconMaster'


interface Widget {
    value: string
    label: string
    code?: string
    image?: string
}
interface Props {
    widget: Widget
    items?: CategoryItem[]
    onSave: (payload: any) => void
    initialData?: any
    onCancel?: () => void;
}

const MAX_ITEMS = 12;

const CategoryGrid = ({ widget, items = [], onSave, initialData }: Props) => {
    const navigate = useNavigate()
    const uploadMutation = useUploadFiles()
    const notification = useNotificationBar();

    const {
        register,
        control,
        watch,
        setValue,
        getValues,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        trigger,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(categoryGridFormSchema),
        defaultValues: {
            ...CATEGORY_GRID_DEFAULTS,
            widgetIcon: '', // ✅ IMPORTANT
        },
        mode: 'onTouched',
    });

    const { data: iconsResponse } = useGetIcons();

    const icons =
        Array.isArray(iconsResponse?.data?.results)
            ? iconsResponse.data.results
            : [];

    const enabled = watch('enabled')
    const contentType = watch('contentType')
    const sourceType = watch('sourceType')
    const sortingMethod = watch('sortingMethod')
    const ctaAction = watch('ctaAction')


    const [widgetIconPreview, setWidgetIconPreview] = useState<string | null>(null)
    const [widgetIconName, setWidgetIconName] = useState<string>('');
    const [categoryItems, setCategoryItems] = useState<CategoryItem[]>(items)
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')

    const isInitialized = useRef(false)
    const lastInitialDataRef = useRef<any>(null)
    const filterParams = {
        "pagination": {
            "pageIndex": 0,
            "pageSize": 100
        },
        "fields": [
            {
                "value": 1,
                "id": "status"
            }
        ]
    }
    const { data: categoryData, isLoading: isLoadingCategories } = useGetCategoryData(filterParams, {
        keepPreviousData: true,
    })


    const categoryOptions = useMemo(() => {
        return (
            categoryData?.data?.results?.map((category: any) => ({
                value: category.id?.toString() || '',
                label: category.name || '',
                image: category.thumbnail || category.chipImageUrl || '',
            })) || []
        )
    }, [categoryData])

    const availableCategoryOptions = useMemo(() => {
        const selectedIds = new Set(categoryItems.map(item => item.id))
        return categoryOptions.filter(option => !selectedIds.has(option.value))
    }, [categoryOptions, categoryItems])

    const getRelativeS3Path = (url: string): string => {
        try {
            return new URL(url).pathname.replace(/^\/+/, '');
        } catch {
            return url;
        }
    };
    const handleCategorySelect = (option: { value: string; label: string }) => {
        if (!option.value || categoryItems.length >= MAX_ITEMS || isLoadingCategories) return

        const selectedCategory = categoryOptions.find(
            (opt) => opt.value === option.value
        )

        if (selectedCategory) {
            const newItem: CategoryItem = {
                id: selectedCategory.value,
                title: selectedCategory?.label,
                thumbnail: selectedCategory.image
                    ? getRelativeS3Path(selectedCategory.image)
                    : '',
                redirection_to: 'CATEGORY' as any,
                redirection_id: selectedCategory.value,
            }
            setCategoryItems(prev => [...prev, newItem])
            setSelectedCategoryId('')
        }
    }

    const moveItem = (from: number, to: number) => {
        setCategoryItems(prev => {
            if (to < 0 || to >= prev.length) return prev

            const updated = [...prev]
                ;[updated[from], updated[to]] = [updated[to], updated[from]]
            return updated
        })
    }

    const deleteItem = (index: number) => {
        setCategoryItems(prev => prev.filter((_, i) => i !== index))
    }

    const handleEnabledChange = useCallback((checked: boolean) => {
        setValue('enabled', checked)
    }, [setValue])

    const handleContentTypeChange = useCallback((value: string) => {
        setValue('contentType', value, { shouldValidate: true })
    }, [setValue])

    const handleSourceTypeChange = useCallback((option: { value: string; label: string }) => {
        setValue('sourceType', option.value, { shouldValidate: true })
        clearErrors('sourceType')
        if (option.value === 'All') {
            trigger('sortingMethod')
        } else {
            clearErrors('sortingMethod')
        }
    }, [setValue, clearErrors, trigger])

    const handleSortingMethodChange = useCallback((option: { value: string; label: string }) => {
        setValue('sortingMethod', option.value, { shouldValidate: true })
        clearErrors('sortingMethod')
    }, [setValue, clearErrors])

    const handleCtaActionChange = useCallback((option: { value: string; label: string }) => {
        setValue('ctaAction', option.value, { shouldValidate: true })
        clearErrors('ctaAction')
    }, [setValue, clearErrors])

    // Initialize form fields from initialData when in edit mode
    useEffect(() => {
        if (lastInitialDataRef.current !== initialData) {
            isInitialized.current = false
            lastInitialDataRef.current = initialData
        }

        if (!initialData) {
            if (isInitialized.current) {
                // Reset form to defaults
                reset({
                    ...CATEGORY_GRID_DEFAULTS,
                    widgetIcon: '',
                })
                setCategoryItems([])

                setWidgetIconPreview(null)
                isInitialized.current = false
            }
            return
        }

        // Skip if already initialized with this data
        if (isInitialized.current) return

        const widgetData = initialData.data || {}

        // Prepare form values
        const nameValue = initialData.name || widgetData.widget_name || widget.label || ''
        const contentTypeValue = widgetData.content_type
            ? (widgetData.content_type.toUpperCase() === 'STATIC'
                ? 'Static'
                // : widgetData.content_type.toUpperCase() === 'CONTEXTUAL'
                //     ? 'Contextual'
                : widgetData.content_type)
            : CATEGORY_GRID_DEFAULTS.contentType

        const sourceTypeValue = widgetData.source_type
            ? (widgetData.source_type.toUpperCase() === 'MANUAL'
                ? 'Manual'
                : widgetData.source_type.toUpperCase() === 'ALL'
                    ? 'All'
                    : widgetData.source_type)
            : CATEGORY_GRID_DEFAULTS.sourceType

        // Normalize sorting method to match option values
        const normalizeSortingMethod = (method: string | undefined): string => {
            if (!method) return CATEGORY_GRID_DEFAULTS.sortingMethod

            // Find matching option by label (case-insensitive) or exact value match
            const normalized = method.toUpperCase()
            const option = SORTING_OPTIONS.find(
                opt => opt.value.toUpperCase() === normalized ||
                    opt.label.toUpperCase() === normalized
            )

            return option ? option.value : CATEGORY_GRID_DEFAULTS.sortingMethod
        }

        // Reset form with fetched data
        reset({
            widgetName: nameValue,
            widgetTitle: widgetData.widget_title || CATEGORY_GRID_DEFAULTS.widgetTitle,
            enabled: initialData.status !== undefined ? initialData.status === 1 : CATEGORY_GRID_DEFAULTS.enabled,
            contentType: contentTypeValue,
            sourceType: sourceTypeValue,
            sortingMethod: normalizeSortingMethod(widgetData.sorting_method),
            ctaLabel: widgetData.cta_label || CATEGORY_GRID_DEFAULTS.ctaLabel,
            ctaAction: widgetData.cta_action || CATEGORY_GRID_DEFAULTS.ctaAction,
            widgetIcon: widgetData.widget_icon || '',
        })


        // Set category items
        if (widgetData.widget_items && Array.isArray(widgetData.widget_items)) {
            setCategoryItems(widgetData.widget_items)
        } else {
            setCategoryItems([])
        }

        // REMOVE any reference to setWidgetIcon in this effect, since we're now fully controlling it through RHF and the widgetIcon field

        if (widgetData.widget_icon) {

            const iconPath = widgetData.widget_icon;

            setValue('widgetIcon', iconPath, { shouldValidate: true });

            const previewURL = iconPath.startsWith('http')
                ? iconPath
                : getImageSrc(iconPath);

            setWidgetIconPreview(previewURL);
            clearErrors('widgetIcon');

            const cleanPath = iconPath.split('/').pop();

            const matchedIcon = icons.find((icon: any) => {
                const iconValue =
                    icon.icon || icon.path || icon.image || '';

                return iconValue.includes(cleanPath);
            });
            // const fileName = iconPath.split('/').pop() || '';
            // // Remove extension
            // const nameWithoutExt = fileName.replace('.svg', '');
            // // Get first part before underscore → trophy
            // const baseName = nameWithoutExt.split('_')[0];

            // setWidgetIconName(baseName.charAt(0).toUpperCase() + baseName.slice(1) || '');
        } else {
            setValue('widgetIcon', '', { shouldValidate: true });
            setWidgetIconPreview(null);
            setWidgetIconName('');
        }
        isInitialized.current = true
    }, [initialData, reset, widget.label, icons])

    useEffect(() => {
        return () => {
            if (widgetIconPreview && widgetIconPreview.startsWith('blob:')) {
                URL.revokeObjectURL(widgetIconPreview)
            }
        }
    }, [widgetIconPreview])

    const onSubmit = async (data: any, selectedIcon: any) => {
        const isManual = data.sourceType === 'Manual';
        const isAll = data.sourceType === 'All';

        // Manual source type validation
        if (isManual && categoryItems.length === 0) {
            setError('sourceType', {
                type: 'manual',
                message: 'At least one category is required when source type is Manual',
            });

            notification.push({
                title: 'Error',
                message: 'At least one category is required when source type is Manual',
                type: 'error',
            });

            return;
        }

        // All source type validation
        if (isAll && !data.sortingMethod) {
            setError('sortingMethod', {
                type: 'required',
                message: 'Sorting method is required when source type is All',
            });

            notification.push({
                message: 'Sorting method is required when source type is All',
                type: 'error',
            });

            return;
        }

        // Icon validation
        if (!data.widgetIcon) {
            setError('widgetIcon', {
                type: 'required',
                message: 'Widget icon is required',
            });

            notification.push({
                message: 'Widget icon is required',
                type: 'error',
            });

            return;
        }

        const iconPath = getRelativeIconPath(data.widgetIcon);

        const payload = {
            name: data.widgetName,
            widget_type: parseInt(widget.value),
            widget_code: widget.code,
            status: data.enabled ? 1 : 0,
            data: {
                widget_name: data.widgetName,
                widget_code: widget.code,
                widget_title: data.widgetTitle,
                widget_icon: iconPath, // ✅ SINGLE SOURCE
                content_type: data.contentType?.toUpperCase(),
                cta_label: data.ctaLabel,
                cta_action: data.ctaAction,
                source_type: data.sourceType?.toUpperCase(),
                widget_items: data.sourceType === 'Manual' ? categoryItems : [],
                sorting_method: isAll ? data.sortingMethod : undefined
            },
        };


        if (data.sourceType === 'All') {
            payload.data.sorting_method = data.sortingMethod
        }
        onSave(payload)

        // ✅ SUCCESS NOTIFICATION
        notification.push({
            title: 'Success',
            message: 'Category Grid widget saved successfully',
            type: 'success',
        })

    }

    const handleCancel = () => {
        onSave({ ...initialData, _cancel: true });
    };

    const widgetIconValue = watch('widgetIcon');

    useEffect(() => {
        if (!widgetIconValue || !icons.length) {
            setWidgetIconName('');
            return;
        }

        const cleanPath = widgetIconValue.split('/').pop() || '';

        const matchedIcon = icons.find((icon: any) => {
            const iconValue =
                icon.icon || icon.path || icon.image || '';

            return iconValue.includes(cleanPath);
        });

        if (matchedIcon) {
            setWidgetIconName(matchedIcon.name || '');
        }
    }, [widgetIconValue, icons]);

    useEffect(() => {
        // If value is empty, clear preview
        if (!widgetIconValue) {
            setWidgetIconPreview(null);
            return;
        }

        // Prevent double-update conflicts with manual preview setting
        if (widgetIconValue && !widgetIconValue.startsWith("blob:")) {
            const previewURL = widgetIconValue.startsWith("http")
                ? widgetIconValue
                : getImageSrc(widgetIconValue);

            setWidgetIconPreview(previewURL);
        }
    }, [widgetIconValue]);

    /* ---------- Render ---------- */

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="space-y-6 col-span-2">
                    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">

                        <div className="space-y-6">
                            <div>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-200 block mb-3">
                                    Content Type <span className="text-red-700">*</span>
                                    <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                                            All published categories or admin-selected categories only. Fixed grid layout with "View All Topics" if greater then 6 categories.
                                        </div>
                                    </span>
                                </label>
                                <div className="flex flex-col">
                                    <div className="flex items-center gap-4">
                                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                                            <input
                                                type="radio"
                                                {...register('contentType', { required: 'Content type is required' })}
                                                value="Static"
                                                checked={contentType === 'Static'}
                                                onChange={() => handleContentTypeChange('Static')}
                                            />
                                            Static
                                        </label>
                                        {/* <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                                            <input
                                                type="radio"
                                                {...register('contentType', { required: 'Content type is required' })}
                                                value="Contextual"
                                                checked={contentType === 'Contextual'}
                                                onChange={() => handleContentTypeChange('Contextual')}
                                            />
                                            Contextual
                                        </label> */}
                                    </div>
                                    {errors.contentType && (
                                        <p className="text-xs text-red-600 mt-1">{errors.contentType.message}</p>
                                    )}
                                </div>
                            </div>
                            <div className="">
                                <div className="flex items-center gap-1 mb-3">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                        Widget Title <span className="text-red-700">*</span>
                                    </label>

                                    <div className="relative group shrink-0">
                                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />
                                        <div
                                            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64
  rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
  p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg
  opacity-0 invisible pointer-events-none
  group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto
  transition-all duration-150 z-50">
                                            Static text for widget header (e.g., 'Hottest Topics'). Displays prominently above category grid.
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col flex-1 max-w-xs">
                                    <input
                                        type="text"
                                        {...register('widgetTitle', { required: 'Widget title is required' })}
                                        className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter widget title"
                                    />
                                    {errors.widgetTitle && (
                                        <p className="text-xs text-red-600 mt-1">{errors.widgetTitle.message}</p>
                                    )}
                                </div>
                            </div>

                            <div className="">
                                <div className="flex items-center gap-1 mb-3">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-200 block">
                                        Widget Icon <span className="text-red-700">*</span>
                                    </label>
                                    <div className="relative group flex items-center">
                                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />
                                        <div
                                            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64
  rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
  p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg
  opacity-0 invisible pointer-events-none
  group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto
  transition-all duration-150 z-50">
                                            <p className='font-semibold mb-1'>Select icon from library for widget header. Appears next to widget title. No auto-sourcing from metadata.</p>
                                            <p className="font-semibold mb-1">Image requirements:</p>
                                            <ul className="space-y-1">

                                                <li className="flex gap-1">
                                                    <span>•</span>
                                                    <span>Formats: SVG</span>
                                                </li>
                                                <li className="flex gap-1">
                                                    <span>•</span>
                                                    <span>File size: 500 KB – 1 MB</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2 max-w-xs">
                                    <Controller
                                        name="widgetIcon"
                                        control={control}
                                        render={({ field }) => {
                                            const iconValue = field.value;

                                            return (
                                                <div className="relative">
                                                    {/* If icon selected → show inside field */}
                                                    {iconValue ? (
                                                        <div className="flex items-center justify-between border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800">
                                                            <div className="flex items-center gap-2">
                                                                <img
                                                                    src={
                                                                        iconValue.startsWith('http')
                                                                            ? iconValue
                                                                            : getImageSrc(iconValue)
                                                                    }
                                                                    alt="Selected icon"
                                                                    className="w-6 h-6 object-contain dark:text-white dark:bg-white"
                                                                />
                                                                <span className="text-sm text-gray-700 dark:text-gray-200">
                                                                    {widgetIconName || 'Icon selected'}
                                                                </span>
                                                            </div>

                                                            {/* Remove icon */}
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    field.onChange('');
                                                                    setWidgetIconPreview(null);
                                                                    setWidgetIconName('');
                                                                    clearErrors('widgetIcon');
                                                                }}
                                                                className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        /* Show dropdown when no icon selected */
                                                        <IconDropdown
                                                            // value={iconValue}
                                                            onChange={(selectedIcon: any) => {
                                                                const iconKey =
                                                                    selectedIcon?.path ||
                                                                    selectedIcon?.image ||
                                                                    selectedIcon?.icon ||
                                                                    '';

                                                                field.onChange(iconKey);

                                                                if (iconKey) {
                                                                    const preview = iconKey.startsWith('http')
                                                                        ? iconKey
                                                                        : getImageSrc(iconKey);

                                                                    setWidgetIconPreview(preview);
                                                                    setWidgetIconName(selectedIcon?.name || '');
                                                                    clearErrors('widgetIcon');
                                                                } else {
                                                                    setWidgetIconPreview(null);
                                                                    setWidgetIconName('');
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        }}
                                    />

                                    {errors.widgetIcon && (
                                        <p className="text-xs text-red-600">{errors.widgetIcon.message}</p>
                                    )}
                                </div>
                            </div>

                            {/*
                            <div className="flex items-center gap-3">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Enabled
                                </label>
                                <ToggleField
                                    checked={enabled}
                                    onChange={(e) => handleEnabledChange(e.target.checked)}
                                />
                            </div> */}
                        </div>
                    </div>
                </div>
                <div className="space-y-6 col-span-3">
                    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900 grid gap-2">
                        <div className="">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-200 block mb-2">
                                Source Type <span className="text-red-700">*</span>
                                <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                                    <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                                        All: Display all published categories ordered by priority. <br />
                                        Manual: Admin selects specific category IDs to display. Shows only selected categories.
                                    </div>
                                </span>
                            </label>
                            <div className="flex flex-col flex-1 max-w-xs">
                                <SingleSelect
                                    value={sourceType}
                                    options={SOURCE_TYPE_OPTIONS}
                                    placeholder="Select Source Type"
                                    onChange={handleSourceTypeChange}
                                />
                                {errors.sourceType && (
                                    <p className="text-xs text-red-600 mt-1">{errors.sourceType.message}</p>
                                )}
                            </div>
                        </div>

                        {sourceType === 'All' && (
                            <div className="">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-200 block mb-3">
                                    Sorting Method  <span className="text-red-700">*</span>
                                    <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                                    <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                                        Select how the items should be ordered when displayed to users.
                                    </div>
                                </span>
                                </label>
                                <div className="flex flex-col flex-1 max-w-xs">
                                    <SingleSelect
                                        value={sortingMethod}
                                        options={SORTING_OPTIONS}
                                        placeholder="Select Option"
                                        onChange={handleSortingMethodChange}
                                    />
                                    {errors.sortingMethod && (
                                        <p className="text-xs text-red-600 mt-1">{errors.sortingMethod.message}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-200 block mb-2">
                                CTA Label <span className="text-red-700">*</span>
                                <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                                    <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64
                                        rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                                        p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg
                                        opacity-0 invisible group-hover:opacity-100 group-hover:visible
                                        transition-all duration-150 z-50 pointer-events-none">
                                        Text for 'View All Topics' button (e.g., 'Explore All', 'See More'). Shows if total categories greater then 6.
                                    </div>
                                </span>

                            </label>
                            <div className="flex flex-col flex-1 max-w-xs">
                                <input
                                    type="text"
                                    {...register('ctaLabel', { required: 'CTA label is required' })}
                                    className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                                    placeholder="Enter CTA label"
                                />
                                {errors.ctaLabel && (
                                    <p className="text-xs text-red-600 mt-1">{errors.ctaLabel.message}</p>
                                )}
                            </div>
                        </div>
                        <div className="">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-200 block mb-2">
                                CTA Action <span className="text-red-700">*</span>
                                <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub] z-10">
                                    <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64
                                        rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                                        p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg
                                        opacity-0 invisible group-hover:opacity-100 group-hover:visible
                                        transition-all duration-150 z-50 pointer-events-none ">
                                        Opens bottom sheet listing all published categories in same sort order as widget.
                                    </div>
                                </span>
                            </label>
                            <div className="flex flex-col flex-1 max-w-xs">
                                <SingleSelect
                                    value={ctaAction}
                                    options={CTA_ACTION_OPTIONS}
                                    placeholder="Select CTA Action"
                                    onChange={handleCtaActionChange}
                                />
                                {errors.ctaAction && (
                                    <p className="text-xs text-red-600 mt-1">{errors.ctaAction.message}</p>
                                )}
                            </div>
                        </div>
                        {sourceType === 'Manual' && (
                            <>
                                <div className="">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-200 block mb-2">
                                        Select Category
                                    </label>
                                    <div className="flex flex-col flex-1 max-w-xs">
                                        <SingleSelect
                                            value={selectedCategoryId}
                                            options={availableCategoryOptions}
                                            //disabled={false}
                                            placeholder={
                                                isLoadingCategories
                                                    ? 'Loading categories...'
                                                    : categoryItems.length >= MAX_ITEMS
                                                        ? 'Maximum items reached'
                                                        : 'Select Category'
                                            }
                                            onChange={handleCategorySelect}
                                        />
                                    </div>
                                </div>


                                <div className='flex items-center justify-between'>
                                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                        Selected Category
                                    </h3>
                                    {/* Tooltip */}
                                    <div className="relative flex-item group ml-2 mt-0">
                                        {/* Item Count */}
                                        <div className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 ">

                                            <span
                                                className={`
                          rounded-full px-2 py-0.5 text-xs font-medium
                          ${categoryItems.length >= MAX_ITEMS
                                                        ? 'bg-red-100 text-red-700'
                                                        : 'bg-blue-100 text-blue-700'}
                      `}
                                            > ({categoryItems.length}/{MAX_ITEMS})</span>
                                        </div>

                                        <div className="absolute bottom-full right-0 -translate-y-0 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50
                                                                    ">
                                            You can select up to {MAX_ITEMS} items <span className='font-medium'>({categoryItems?.length} selected)</span>.
                                        </div>
                                    </div>
                                </div>



                                <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                                    {categoryItems.length === 0 ? (


                                        <p className="text-sm text-gray-500">No items added yet.</p>
                                    ) : (
                                        categoryItems.map((item, index) => {
                                            const categoryName = categoryOptions.find(
                                                (opt) => opt.value === item.id
                                            )?.label || ''

                                            return (
                                                <CarouselItem
                                                    key={item.id}
                                                    item={item}
                                                    index={index}
                                                    total={categoryItems.length}
                                                    onMoveUp={() => moveItem(index, index - 1)}
                                                    onMoveDown={() => moveItem(index, index + 1)}
                                                    onDelete={() => deleteItem(index)}
                                                >
                                                    <div key={item.id} className="flex items-center gap-3">
                                                        <img
                                                            // src={item.thumbnail}
                                                            src={getImageSrc(item.thumbnail)}
                                                            alt="Thumbnail"
                                                            className="w-20 h-12 object-cover rounded border"
                                                        />

                                                        <div>
                                                            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                                                {categoryName || item.redirection_to}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {item.redirection_to} · ID: {item.redirection_id}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </CarouselItem>
                                            )
                                        })
                                    )}
                                </div>

                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-slate-600">
                <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-2 bg-gray-300 dark:bg-slate-600 text-gray-800 dark:text-white rounded hover:bg-gray-400 dark:hover:bg-slate-500"
                >
                    Cancel
                </button>

                <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Save
                </button>
            </div>
        </form>
    )
}

export default CategoryGrid